/**
 * Komal OS - Project Store
 * Manages project list/details state against the real API
 */

import { create } from 'zustand';
import * as projectService from '../services/projectService';

const useProjectStore = create((set, get) => ({
  projects: [],
  selectedProject: null,
  isLoading: false,
  error: null,

  fetchProjects: async (status) => {
    set({ isLoading: true, error: null });
    try {
      const response = await projectService.getProjects(status);
      set({ projects: response.data.projects || [], isLoading: false });
      return response.data.projects;
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to load projects';
      set({ error: message, isLoading: false });
      return [];
    }
  },

  fetchProject: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await projectService.getProject(id);
      set({ selectedProject: response.data.project, isLoading: false });
      return response.data.project;
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to load project';
      set({ error: message, isLoading: false });
      return null;
    }
  },

  createProject: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await projectService.createProject(data);
      await get().fetchProjects();
      set({ isLoading: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create project';
      set({ error: message, isLoading: false });
      return { success: false, error: message };
    }
  },

  updateProject: async (id, data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await projectService.updateProject(id, data);
      set({ isLoading: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update project';
      set({ error: message, isLoading: false });
      return { success: false, error: message };
    }
  },

  setProjectStatus: async (id, status) => {
    set({ isLoading: true, error: null });
    try {
      const response = await projectService.setProjectStatus(id, status);
      set((state) => ({
        projects: state.projects.filter((p) => p.id !== id),
        selectedProject: state.selectedProject?.id === id ? { ...state.selectedProject, status } : state.selectedProject,
        isLoading: false,
      }));
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update project status';
      set({ error: message, isLoading: false });
      return { success: false, error: message };
    }
  },

  cancelProject: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await projectService.cancelProject(id);
      await get().fetchProjects();
      set({ isLoading: false });
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to remove project';
      set({ error: message, isLoading: false });
      return { success: false, error: message };
    }
  },

  clearError: () => set({ error: null }),
}));

export default useProjectStore;
